import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import supportService from '../services/supportService';
import { useUI } from '../providers/UIProvider';
import { useTranslation } from '../i18n/useTranslation';
import useEllaDucking from '../hooks/useEllaDucking';

const GOLD = '#d4a843';
const STORAGE_KEY = 'maestro_tour_progress';
const STEP_PAUSE_MS = 750;

function getTimeOfDay() {
  const h = new Date().getHours();
  return h < 12 ? 'morning' : h < 17 ? 'afternoon' : 'evening';
}

function findGuideElement(guideId) {
  let el = document.querySelector(`[data-guide-id="${guideId}"]`);
  if (el) return el;

  const sidebarMap = {
    'sidebar-home': 'home', 'sidebar-settings': 'settings', 'sidebar-support': 'support',
    'sidebar-devices': 'devices', 'sidebar-prism': 'prism',
    'sidebar-genres': 'genres', 'sidebar-history': 'history', 'sidebar-qobuz': 'qobuz',
    'sidebar-live_radio': 'live_radio', 'sidebar-ella_radio': 'ella_radio',
    'sidebar-octave_radio': 'octave_radio', 'sidebar-my_playlists': 'my_playlists',
    'sidebar-audiophile_playlists': 'audiophile_playlists',
    'sidebar-genre_playlists': 'genre_playlists', 'sidebar-mood_playlists': 'mood_playlists',
    'sidebar-season_playlists': 'season_playlists',
    'sidebar-valet_for_you': 'valet_for_you',
    'sidebar-valet_trending': 'valet_trending', 'sidebar-valet_new_releases': 'valet_new_releases',
    'sidebar-valet_community': 'valet_community', 'sidebar-valet_personalize': 'valet_personalize',
    'sidebar-favorites': 'favorites',
    'sidebar-stream_playlists': 'stream_playlists', 'sidebar-stream_albums': 'stream_albums',
    'sidebar-stream_artists': 'stream_artists',
  };

  const mapped = sidebarMap[guideId];
  if (mapped) {
    el = document.querySelector(`[data-sidebar-id="${mapped}"]`);
    if (el) return el;
  }

  if (guideId === 'mini-player') el = document.querySelector('.mini-player-container');
  return el;
}

function scrollIntoView(el) {
  try {
    el.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
  } catch {
    el.scrollIntoView(true);
  }
}

export default function GuidedTour({ onClose, userName }) {
  const [steps, setSteps] = useState([]);
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(true);
  const [speaking, setSpeaking] = useState(false);
  const [paused, setPaused] = useState(false);
  const [greetingPhase, setGreetingPhase] = useState(true);
  const [serverGreeting, setServerGreeting] = useState(null);
  const greetingDoneRef = useRef(false);
  const audioRef = useRef(null);
  const audioUrlRef = useRef(null);
  const mountedRef = useRef(true);
  const arrowRef = useRef(null);
  const ringRef = useRef(null);
  const autoAdvanceRef = useRef(true);
  const stepsRef = useRef([]);
  const preloadCacheRef = useRef({});
  const { sidebarOpen, setSidebarOpen, setShowSettings, setShowDevicePicker, setShowQueueSheet } = useUI();
  const settingsTimerRef = useRef(null);
  const stepPauseTimerRef = useRef(null);
  const skipNextPauseRef = useRef(false);
  const playGenRef = useRef(0);
  const { lang } = useTranslation();

  const ellaDucking = useEllaDucking();

  const [agentVolume, setAgentVolume] = useState(80);
  const agentVolumeRef = useRef(0.8);

  // Raw progress from audio timeupdate (jumpy)
  const [rawProgress, setRawProgress] = useState(0);
  // Smoothed progress for display (interpolated)
  const [smoothProgress, setSmoothProgress] = useState(0);
  const targetProgressRef = useRef(0);
  const smoothRef = useRef(0);

  // Smooth interpolation loop (20fps)
  useEffect(() => {
    const interval = setInterval(() => {
      const target = targetProgressRef.current;
      const cur = smoothRef.current;
      const diff = target - cur;
      if (Math.abs(diff) < 0.003) {
        if (cur !== target) { smoothRef.current = target; setSmoothProgress(target); }
      } else {
        const next = cur + diff * 0.22;
        smoothRef.current = next;
        setSmoothProgress(next);
      }
    }, 50);
    return () => clearInterval(interval);
  }, []);

  // Keep targetProgressRef in sync with raw
  useEffect(() => { targetProgressRef.current = rawProgress; }, [rawProgress]);

  const greetingText = useMemo(() => {
    const first = userName?.trim().split(' ')[0] || '';
    return first
      ? `Hi ${first}.\n\n\nWelcome to Maestro.\n\n\nI am Ella, your personal music assistant.\n\n\n\n\nTogether, we're going to make some magic.\n\n\nBut first, let me show you around.`
      : `Hi.\n\n\nWelcome to Maestro.\n\n\nI am Ella, your personal music assistant.\n\n\n\n\nTogether, we're going to make some magic.\n\n\nBut first, let me show you around.`;
  }, [userName]);

  useEffect(() => {
    mountedRef.current = true;
    window.dispatchEvent(new CustomEvent('maestro:tour-active'));
    ellaDucking.startDuck();

    return () => {
      mountedRef.current = false;
      window.dispatchEvent(new CustomEvent('maestro:tour-ended'));
      ellaDucking.stopDuck();
    };
  }, [ellaDucking]);

  useEffect(() => { stepsRef.current = steps; }, [steps]);

  const prevLangRef = useRef(lang);

  useEffect(() => {
    let cancelled = false;
    const isLangChange = prevLangRef.current !== lang;
    prevLangRef.current = lang;

    if (isLangChange) {
      stopAudio();
      cleanupHighlight();
      setLoading(true);
      preloadCacheRef.current = {};
      setServerGreeting(null);
    }

    (async () => {
      try {
        const data = await supportService.getTourSteps(lang, userName);
        if (cancelled || !mountedRef.current) return;
        if (data?.steps) {
          setSteps(data.steps);
          if (data.greeting) setServerGreeting(data.greeting);
          if (!isLangChange) {
            const saved = localStorage.getItem(STORAGE_KEY);
            if (saved) {
              const idx = parseInt(saved, 10);
              if (idx > 0 && idx < data.steps.length) setCurrentStep(idx);
            }
          }
          // Preload Home (step 1) audio during greeting so it's instant
          if (data.steps[1]) {
            const key = `${data.steps[1].id}:${lang}`;
            if (!preloadCacheRef.current[key]) {
              supportService.getTourStepAudio(data.steps[1].id, lang, userName).then(u => {
                if (u && mountedRef.current) preloadCacheRef.current[key] = u;
              }).catch(() => {});
            }
          }
        }
      } catch (err) {
        console.warn('[GuidedTour] Failed to load steps:', err);
      } finally {
        if (!cancelled && mountedRef.current) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [lang]);

  // --- Greeting TTS (waits for translated text on non-English) ---
  const effectiveGreeting = serverGreeting || (lang === 'en' ? greetingText : null);

  useEffect(() => {
    if (!greetingPhase || !effectiveGreeting) return;
    let cancelled = false;

    (async () => {
      try {
        const url = await supportService.textToSpeech(effectiveGreeting);
        if (cancelled || !mountedRef.current) return;
        if (url) {
          setSpeaking(true);
          setRawProgress(0); smoothRef.current = 0; setSmoothProgress(0);
          const audio = new Audio(url);
          audio.volume = agentVolumeRef.current;
          audioRef.current = audio;
          audioUrlRef.current = url;

          audio.ontimeupdate = () => {
            if (audio.duration > 0) setRawProgress(audio.currentTime / audio.duration);
          };
          const transitionToTour = () => {
            if (cancelled || !mountedRef.current) return;
            audioRef.current = null;
            audioUrlRef.current = null;
            setSpeaking(false);
            setRawProgress(1); smoothRef.current = 1; setSmoothProgress(1);
            if (stepsRef.current.length > 0) {
              setTimeout(() => {
                if (cancelled || !mountedRef.current) return;
                skipNextPauseRef.current = true;
                setGreetingPhase(false);
                setRawProgress(0); smoothRef.current = 0; setSmoothProgress(0);
                setCurrentStep(1);
              }, 1200);
            } else {
              greetingDoneRef.current = true;
            }
          };
          audio.onended = transitionToTour;
          audio.onerror = transitionToTour;
          await audio.play();
        } else {
          setRawProgress(1); smoothRef.current = 1; setSmoothProgress(1);
          setTimeout(() => {
            if (!cancelled && mountedRef.current) greetingDoneRef.current = true;
          }, 3000);
        }
      } catch {
        if (!cancelled && mountedRef.current) {
          setRawProgress(1); smoothRef.current = 1; setSmoothProgress(1);
          greetingDoneRef.current = true;
        }
      }
    })();

    return () => { cancelled = true; };
  }, [greetingPhase, effectiveGreeting]);

  // Fallback: if greeting finishes but steps weren't loaded yet, poll
  useEffect(() => {
    if (!greetingPhase) return;
    const interval = setInterval(() => {
      if (greetingDoneRef.current && !loading && steps.length > 0) {
        clearInterval(interval);
        skipNextPauseRef.current = true;
        setGreetingPhase(false);
        setRawProgress(0); smoothRef.current = 0; setSmoothProgress(0);
        setCurrentStep(1);
      }
    }, 100);
    return () => clearInterval(interval);
  }, [greetingPhase, loading, steps.length]);

  const cleanupHighlight = useCallback(() => {
    if (arrowRef.current) { arrowRef.current.remove(); arrowRef.current = null; }
    if (ringRef.current) { ringRef.current.remove(); ringRef.current = null; }
    document.querySelectorAll('[data-tour-highlight]').forEach(el => el.remove());
  }, []);

  const stopAudio = useCallback(() => {
    if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; }
    audioUrlRef.current = null;
    setSpeaking(false);
  }, []);

  const cleanupSettingsTimer = useCallback(() => {
    if (settingsTimerRef.current) { clearTimeout(settingsTimerRef.current); settingsTimerRef.current = null; }
  }, []);

  const demoTimerRef = useRef(null);
  const cleanupDemoTimer = useCallback(() => {
    if (demoTimerRef.current) { clearTimeout(demoTimerRef.current); demoTimerRef.current = null; }
  }, []);

  const cleanupStepPause = useCallback(() => {
    if (stepPauseTimerRef.current) { clearTimeout(stepPauseTimerRef.current); stepPauseTimerRef.current = null; }
  }, []);

  useEffect(() => {
    return () => {
      stopAudio();
      cleanupHighlight();
      cleanupSettingsTimer();
      cleanupDemoTimer();
      cleanupStepPause();
      setSidebarOpen(false);
      setShowSettings(false);
      setShowDevicePicker(false);
      setShowQueueSheet(false);
      ellaDucking.stopDuck();
    };
  }, [stopAudio, cleanupHighlight, cleanupSettingsTimer, cleanupDemoTimer, cleanupStepPause, setSidebarOpen, setShowSettings, setShowDevicePicker, setShowQueueSheet, ellaDucking]);

  const highlightElement = useCallback((guideId, { pointDown = false } = {}) => {
    cleanupHighlight();
    const el = findGuideElement(guideId);
    if (!el) return;

    scrollIntoView(el);

    setTimeout(() => {
      if (!mountedRef.current) return;
      const rect = el.getBoundingClientRect();

      const isSidebar = guideId.startsWith('sidebar-');
      const arrow = document.createElement('div');
      arrow.setAttribute('data-tour-highlight', 'arrow');
      if (isSidebar) {
        arrow.innerHTML = `<svg width="28" height="28" viewBox="0 0 32 32" fill="none"><path d="M4 16 L16 4 L16 12 L28 12 L28 20 L16 20 L16 28 Z" fill="${GOLD}" stroke="#000" stroke-width="1"/></svg>`;
      } else if (pointDown) {
        arrow.innerHTML = `<svg width="28" height="28" viewBox="0 0 32 32" fill="none"><path d="M16 28 L28 16 L20 16 L20 4 L12 4 L12 16 L4 16 Z" fill="${GOLD}" stroke="#000" stroke-width="1"/></svg>`;
      } else {
        arrow.innerHTML = `<svg width="28" height="28" viewBox="0 0 32 32" fill="none"><path d="M16 4 L28 16 L20 16 L20 28 L12 28 L12 16 L4 16 Z" fill="${GOLD}" stroke="#000" stroke-width="1"/></svg>`;
      }

      let arrowLeft, arrowTopPos, anim;
      if (isSidebar) {
        arrowLeft = rect.right + 10;
        arrowTopPos = rect.top + rect.height / 2 - 14;
        anim = 'tourArrowBounceH 0.8s ease-in-out infinite';
      } else {
        arrowLeft = rect.left + rect.width / 2 - 14;
        arrowTopPos = rect.top - 38;
        anim = 'tourArrowBounce 0.8s ease-in-out infinite';
      }
      Object.assign(arrow.style, {
        position: 'fixed', zIndex: '100001',
        left: `${arrowLeft}px`,
        top: `${arrowTopPos}px`,
        pointerEvents: 'none',
        animation: anim,
        filter: `drop-shadow(0 2px 8px ${GOLD}80)`,
      });

      const ring = document.createElement('div');
      ring.setAttribute('data-tour-highlight', 'ring');
      Object.assign(ring.style, {
        position: 'fixed', zIndex: '100000',
        left: `${rect.left - 5}px`, top: `${rect.top - 5}px`,
        width: `${rect.width + 10}px`, height: `${rect.height + 10}px`,
        borderRadius: '12px',
        border: `2px solid ${GOLD}`,
        boxShadow: `0 0 20px ${GOLD}50, inset 0 0 20px ${GOLD}15`,
        animation: 'tourRingPulse 1.2s ease-in-out infinite',
        pointerEvents: 'none',
      });

      document.body.appendChild(arrow);
      document.body.appendChild(ring);
      arrowRef.current = arrow;
      ringRef.current = ring;
    }, 350);
  }, [cleanupHighlight]);

  const finishTour = useCallback(() => {
    autoAdvanceRef.current = false;
    mountedRef.current = false;
    stopAudio();
    cleanupHighlight();
    cleanupSettingsTimer();
    cleanupDemoTimer();
    cleanupStepPause();
    setSidebarOpen(false);
    setShowSettings(false);
    setShowDevicePicker(false);
    setShowQueueSheet(false);
    setGreetingPhase(false);
    ellaDucking.stopDuck();
    setTimeout(() => cleanupHighlight(), 500);
    try { localStorage.removeItem(STORAGE_KEY); } catch {}
    onClose?.();
  }, [stopAudio, cleanupHighlight, cleanupSettingsTimer, cleanupDemoTimer, cleanupStepPause, setSidebarOpen, setShowSettings, setShowDevicePicker, setShowQueueSheet, onClose, ellaDucking]);

  const advanceToNext = useCallback(() => {
    if (!mountedRef.current || !autoAdvanceRef.current) return;
    setCurrentStep(prev => {
      const next = prev + 1;
      if (next >= stepsRef.current.length) {
        setTimeout(() => finishTour(), 0);
        return prev;
      }
      return next;
    });
  }, [finishTour]);

  const preloadStepAudio = useCallback((stepId) => {
    const key = `${stepId}:${lang}`;
    if (preloadCacheRef.current[key]) return;
    supportService.getTourStepAudio(stepId, lang, userName).then(url => {
      if (url && mountedRef.current) preloadCacheRef.current[key] = url;
    }).catch(() => {});
  }, [lang, userName]);

  const playStepAudio = useCallback(async (stepId, stepIndex) => {
    stopAudio();
    const gen = ++playGenRef.current;
    setSpeaking(true);
    setRawProgress(0); smoothRef.current = 0; setSmoothProgress(0);

    const allSteps = stepsRef.current;
    for (let i = 1; i <= 2; i++) {
      const nextIdx = stepIndex + i;
      if (nextIdx < allSteps.length) preloadStepAudio(allSteps[nextIdx].id);
    }

    const fallbackTextAdvance = () => {
      setSpeaking(false);
      setRawProgress(1); smoothRef.current = 1; setSmoothProgress(1);
      const step = stepsRef.current[stepIndex];
      const readMs = step ? Math.max(3000, Math.min(step.narration.length * 40, 8000)) : 4000;
      setTimeout(() => {
        if (mountedRef.current && gen === playGenRef.current) advanceToNext();
      }, readMs);
    };

    try {
      const key = `${stepId}:${lang}`;
      const cached = preloadCacheRef.current[key];
      const audioUrl = cached || await supportService.getTourStepAudio(stepId, lang, userName);
      if (cached) delete preloadCacheRef.current[key];
      if (!audioUrl || !mountedRef.current || gen !== playGenRef.current) {
        if (gen === playGenRef.current) fallbackTextAdvance();
        return;
      }

      audioUrlRef.current = audioUrl;
      const audio = new Audio(audioUrl);
      audio.volume = agentVolumeRef.current;
      audioRef.current = audio;

      audio.ontimeupdate = () => {
        if (audio.duration > 0) setRawProgress(audio.currentTime / audio.duration);
      };

      audio.onended = () => {
        if (!mountedRef.current) return;
        audioUrlRef.current = null;
        audioRef.current = null;
        setSpeaking(false);
        setRawProgress(1); smoothRef.current = 1; setSmoothProgress(1);
        advanceToNext();
      };

      audio.onerror = () => {
        if (!mountedRef.current) return;
        audioUrlRef.current = null;
        audioRef.current = null;
        fallbackTextAdvance();
      };

      await audio.play();
    } catch {
      if (mountedRef.current) fallbackTextAdvance();
    }
  }, [stopAudio, advanceToNext, preloadStepAudio, lang]);

  const executeStepAction = useCallback((step) => {
    cleanupSettingsTimer();
    cleanupDemoTimer();
    setShowSettings(false);
    setShowDevicePicker(false);
    setShowQueueSheet(false);
    if (step.action) setSidebarOpen(false);

    if (step.action === 'open-settings') {
      setTimeout(() => {
        if (!mountedRef.current) return;
        setShowSettings(true);
        settingsTimerRef.current = setTimeout(() => {
          if (mountedRef.current) setShowSettings(false);
        }, 4000);
      }, 500);
    } else if (step.action === 'open-device-picker') {
      setShowDevicePicker(true);
      demoTimerRef.current = setTimeout(() => {
        if (!mountedRef.current) return;
        setShowDevicePicker(false);
        demoTimerRef.current = setTimeout(() => {
          if (!mountedRef.current) return;
          highlightElement(step.guideId);
        }, 4500);
      }, 6000);
    } else if (step.action === 'open-queue') {
      setTimeout(() => {
        if (!mountedRef.current) return;
        setShowQueueSheet(true);
        demoTimerRef.current = setTimeout(() => {
          if (mountedRef.current) setShowQueueSheet(false);
        }, 5000);
      }, 800);
    } else if (step.action === 'open-help' || step.action === 'open-search' || step.action === 'open-about') {
      const type = step.action === 'open-help' ? 'help' : step.action === 'open-about' ? 'about' : 'search';
      setTimeout(() => {
        if (!mountedRef.current) return;
        window.dispatchEvent(new CustomEvent('maestro:demo-slideout', { detail: { type } }));
      }, 800);
    }
  }, [setSidebarOpen, setShowSettings, setShowDevicePicker, setShowQueueSheet, cleanupSettingsTimer, cleanupDemoTimer, highlightElement]);

  // --- Step effect: highlight, pause, then narrate ---
  useEffect(() => {
    if (!steps.length || loading || paused || greetingPhase) return;
    const step = steps[currentStep];
    if (!step) return;

    setRawProgress(0); smoothRef.current = 0; setSmoothProgress(0);
    const isSidebarStep = step.guideId?.startsWith('sidebar-');
    const isPlaybar = step.guideId === 'mini-player' || step.guideId === 'device-volume' || step.guideId === 'queue-button';

    const SERVICE_GUIDE_IDS = new Set([
      'sidebar-qobuz', 'sidebar-ella_radio',
      'sidebar-live_radio', 'sidebar-octave_radio',
    ]);

    const checkAndProceed = () => {
      if (SERVICE_GUIDE_IDS.has(step.guideId) && !findGuideElement(step.guideId)) {
        advanceToNext();
        return;
      }

      highlightElement(step.guideId, { pointDown: isPlaybar });
      try { localStorage.setItem(STORAGE_KEY, String(currentStep)); } catch {}
      const delay = STEP_PAUSE_MS;
      skipNextPauseRef.current = false;
      if (delay === 0) {
        executeStepAction(step);
        playStepAudio(step.id, currentStep);
      } else {
        stepPauseTimerRef.current = setTimeout(() => {
          if (!mountedRef.current) return;
          executeStepAction(step);
          playStepAudio(step.id, currentStep);
        }, delay);
      }
    };

    if (isSidebarStep && !sidebarOpen) {
      setSidebarOpen(true);
      setTimeout(checkAndProceed, 400);
    } else if (!isSidebarStep && sidebarOpen) {
      setSidebarOpen(false);
      setTimeout(checkAndProceed, 350);
    } else {
      checkAndProceed();
    }
  }, [currentStep, steps.length, loading, paused, greetingPhase]);

  const skipForward = useCallback(() => {
    stopAudio();
    playGenRef.current++;
    cleanupSettingsTimer();
    cleanupDemoTimer();
    cleanupStepPause();
    setSidebarOpen(false);
    setShowSettings(false);
    setShowDevicePicker(false);
    setShowQueueSheet(false);
    autoAdvanceRef.current = true;
    setPaused(false);
    setRawProgress(1); smoothRef.current = 1; setSmoothProgress(1);
    if (greetingPhase) {
      greetingDoneRef.current = true;
      if (!loading && steps.length > 0) {
        setGreetingPhase(false);
        setRawProgress(0); smoothRef.current = 0; setSmoothProgress(0);
        setCurrentStep(1);
      }
      return;
    }
    if (currentStep < steps.length - 1) {
      setCurrentStep(s => s + 1);
    } else {
      finishTour();
    }
  }, [currentStep, steps.length, loading, greetingPhase, stopAudio, finishTour, cleanupSettingsTimer, cleanupDemoTimer, cleanupStepPause, setSidebarOpen, setShowSettings, setShowDevicePicker, setShowQueueSheet]);

  const togglePause = useCallback(() => {
    if (paused) {
      autoAdvanceRef.current = true;
      setPaused(false);
    } else {
      autoAdvanceRef.current = false;
      stopAudio();
      cleanupStepPause();
      setRawProgress(1); smoothRef.current = 1; setSmoothProgress(1);
      setPaused(true);
    }
  }, [paused, stopAudio, cleanupStepPause]);

  const handleAgentVolumeChange = useCallback((e) => {
    const val = parseInt(e.target.value, 10);
    setAgentVolume(val);
    agentVolumeRef.current = val / 100;
    if (audioRef.current) audioRef.current.volume = val / 100;
  }, []);

  const isTitleBarStep = !greetingPhase && steps[currentStep]?.category === 'title-bar';

  // --- Smooth character-based text reveal ---

  function revealText(text, progress) {
    if (progress >= 1) return text;
    const chars = Math.round(text.length * Math.min(progress * 1.03, 1));
    if (chars <= 0) return '\u00A0';
    return text.slice(0, chars);
  }

  const isNarrowScreen = typeof window !== 'undefined' && window.innerWidth < 480;

  const tourStyles = (
    <style>{`
      @keyframes tourArrowBounce { 0%,100% { transform: translateY(0) } 50% { transform: translateY(-10px) } }
      @keyframes tourArrowBounceH { 0%,100% { transform: translateX(0) } 50% { transform: translateX(10px) } }
      @keyframes tourRingPulse { 0%,100% { opacity:0.5; transform:scale(1) } 50% { opacity:1; transform:scale(1.04) } }
      @keyframes tourPanelSlideUp { from { opacity:0; transform: translate(-50%, -48%) scale(0.96) } to { opacity:1; transform: translate(-50%, -50%) scale(1) } }
      @keyframes tourPanelSlideUpTop { from { opacity:0; transform: translateX(-50%) scale(0.96) } to { opacity:1; transform: translateX(-50%) scale(1) } }
      @keyframes tourAudioWave { 0%,100% { height: 4px } 50% { height: 16px } }
      @keyframes tourVinylSpin { from { transform: rotate(0deg) } to { transform: rotate(360deg) } }
      @keyframes tourGlow { 0%,100% { opacity: 0.4 } 50% { opacity: 0.8 } }
      @keyframes tourShimmer { 0% { background-position: -200% center } 100% { background-position: 200% center } }
      @keyframes tourNoteFloat1 { 0%,100% { transform: translate(0, 0) rotate(0deg); opacity: 0.5 } 50% { transform: translate(6px, -8px) rotate(12deg); opacity: 0.8 } }
      @keyframes tourNoteFloat2 { 0%,100% { transform: translate(0, 0) rotate(0deg); opacity: 0.4 } 50% { transform: translate(-5px, -10px) rotate(-8deg); opacity: 0.7 } }
      .tour-vol-slider { -webkit-appearance: none; appearance: none; height: 3px; border-radius: 2px; outline: none; cursor: pointer; }
      .tour-vol-slider::-webkit-slider-thumb { -webkit-appearance: none; width: 12px; height: 12px; border-radius: 50%; background: #f5e6cc; cursor: pointer; box-shadow: 0 1px 4px rgba(0,0,0,0.3); }
      .tour-vol-slider::-moz-range-thumb { width: 12px; height: 12px; border-radius: 50%; background: #f5e6cc; border: none; cursor: pointer; }
    `}</style>
  );

  const overlay = (
    <div
      onClick={(e) => e.stopPropagation()}
      style={{
        position: 'fixed', inset: 0, zIndex: 99999,
        background: 'rgba(0,0,0,0.4)',
        pointerEvents: 'auto',
      }}
    />
  );

  const volumeControl = (
    <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
      <svg width="14" height="14" viewBox="0 0 24 24" fill="rgba(255,255,255,0.35)">
        <path d="M3 9V15H7L12 20V4L7 9H3Z" />
      </svg>
      <input
        type="range" min="0" max="100" value={agentVolume}
        onInput={handleAgentVolumeChange}
        onChange={handleAgentVolumeChange}
        className="tour-vol-slider"
        style={{ width: 60, padding: '8px 0', touchAction: 'none', background: `linear-gradient(90deg, ${GOLD} ${agentVolume}%, rgba(255,255,255,0.12) ${agentVolume}%)` }}
        aria-label="Agent volume"
      />
    </div>
  );

  // --- Album cover color palettes for visual variety ---
  const albumPalettes = [
    { bg: 'linear-gradient(150deg, #c45e3a 0%, #d4813e 25%, #1a3a4a 60%, #0d2233 100%)', accent: '#f0a050', label: '#f5e6cc' },
    { bg: 'linear-gradient(150deg, #2d5a6b 0%, #1a7a6a 25%, #d4813e 60%, #8b3a2a 100%)', accent: '#7dd4c0', label: '#e8f0ee' },
    { bg: 'linear-gradient(150deg, #6b2d4a 0%, #a0445a 25%, #d4813e 60%, #1a3040 100%)', accent: '#e8879a', label: '#f5e0e6' },
    { bg: 'linear-gradient(150deg, #2a4a6b 0%, #3a6a9a 25%, #d4a843 60%, #1a2a3a 100%)', accent: '#7ab0e0', label: '#e0ecf5' },
  ];
  const greetingPalette = albumPalettes[0];

  // --- Greeting phase ---
  if (greetingPhase) {
    return (
      <>
        {tourStyles}
        {overlay}
        <div style={{
          position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
          zIndex: 100002, animation: 'tourPanelSlideUp 0.5s cubic-bezier(0.16, 1, 0.3, 1)',
          width: '90%', maxWidth: 420, pointerEvents: 'none',
        }}>
          <div style={{
            width: '100%', position: 'relative', overflow: 'hidden',
            borderRadius: 16,
            boxShadow: '0 24px 80px rgba(0,0,0,0.7), 0 0 0 1px rgba(255,255,255,0.08)',
            pointerEvents: 'auto',
          }}>
            {/* Album cover art area */}
            <div style={{
              position: 'relative', padding: '28px 24px 20px',
              background: greetingPalette.bg,
              minHeight: 160,
            }}>
              {/* Vinyl record peeking from right edge */}
              <div style={{
                position: 'absolute', right: -30, top: '50%', transform: 'translateY(-50%)',
                width: 120, height: 120, borderRadius: '50%', pointerEvents: 'none',
                background: 'radial-gradient(circle, #222 18%, #111 19%, #1a1a1a 20%, #333 21%, #1a1a1a 40%, #333 41%, #1a1a1a 60%, #333 61%, #1a1a1a 80%, #222 90%)',
                animation: speaking ? 'tourVinylSpin 3s linear infinite' : 'none',
                boxShadow: '-4px 0 20px rgba(0,0,0,0.5)',
                opacity: 0.85,
              }}>
                <div style={{
                  position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
                  width: 28, height: 28, borderRadius: '50%',
                  background: `radial-gradient(circle, ${greetingPalette.accent} 20%, rgba(0,0,0,0.3) 21%, ${greetingPalette.accent}40 50%, transparent 60%)`,
                }}>
                  <div style={{
                    position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
                    width: 6, height: 6, borderRadius: '50%', background: '#111',
                  }} />
                </div>
              </div>

              {/* Floating music notes */}
              <div style={{
                position: 'absolute', top: 18, right: 85, fontSize: 18, pointerEvents: 'none',
                color: 'rgba(255,255,255,0.2)',
                animation: speaking ? 'tourNoteFloat1 2.5s ease-in-out infinite' : 'none',
              }}>&#9835;</div>
              <div style={{
                position: 'absolute', top: 50, right: 100, fontSize: 14, pointerEvents: 'none',
                color: 'rgba(255,255,255,0.15)',
                animation: speaking ? 'tourNoteFloat2 3s ease-in-out infinite' : 'none',
              }}>&#9834;</div>

              {/* Top label */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 20 }}>
                <div style={{
                  width: 6, height: 6, borderRadius: '50%',
                  background: speaking ? '#fff' : 'rgba(255,255,255,0.5)',
                  boxShadow: speaking ? '0 0 8px rgba(255,255,255,0.6)' : 'none',
                  animation: speaking ? 'tourGlow 1.5s ease-in-out infinite' : 'none',
                }} />
                <span style={{
                  fontSize: 10, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase',
                  color: greetingPalette.label, opacity: 0.8,
                }}>
                  GUIDED TOUR
                </span>
                {speaking && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 3, marginLeft: 'auto' }}>
                    {[0,1,2,3,4].map(i => (
                      <div key={i} style={{ width: 2.5, borderRadius: 1, background: 'rgba(255,255,255,0.6)', animation: `tourAudioWave 0.8s ease-in-out ${i * 0.12}s infinite` }} />
                    ))}
                  </div>
                )}
              </div>

              {/* Album title */}
              <div style={{
                fontSize: 32, fontWeight: 900, lineHeight: 1.05, marginBottom: 6,
                color: '#fff', letterSpacing: '-0.02em',
                textShadow: '0 2px 12px rgba(0,0,0,0.4)',
                maxWidth: '70%',
              }}>
                Meet Ella
              </div>
              <div style={{
                fontSize: 13, fontWeight: 600, color: greetingPalette.label, opacity: 0.7,
                letterSpacing: '0.02em', maxWidth: '65%',
              }}>
                Your Personal Music Assistant
              </div>
            </div>

            {/* Content area below the album art */}
            <div style={{
              background: '#111', padding: '16px 22px 18px',
              borderTop: `2px solid ${greetingPalette.accent}30`,
            }}>
              <div style={{ fontSize: 13, lineHeight: 1.65, color: 'rgba(255,255,255,0.65)', marginBottom: 16, minHeight: 24 }}>
                {effectiveGreeting ? revealText(effectiveGreeting, smoothProgress) : '\u00A0'}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <button onClick={skipForward} style={{
                  width: 36, height: 36, borderRadius: 10,
                  border: `1px solid ${greetingPalette.accent}30`,
                  background: `${greetingPalette.accent}12`, color: greetingPalette.accent,
                  cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  transition: 'all 0.2s',
                }}
                onMouseEnter={e => { e.currentTarget.style.background = `${greetingPalette.accent}25`; }}
                onMouseLeave={e => { e.currentTarget.style.background = `${greetingPalette.accent}12`; }}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="13 19 22 12 13 5" /><polyline points="2 19 11 12 2 5" /></svg>
                </button>
                {volumeControl}
                <div style={{ flex: 1 }} />
                <button onClick={finishTour} style={{
                  background: 'none', border: '1px solid rgba(255,255,255,0.1)', cursor: 'pointer',
                  fontSize: 11, fontWeight: 600, color: 'rgba(255,255,255,0.35)', padding: '6px 14px',
                  borderRadius: 8, transition: 'all 0.2s',
                }}
                onMouseEnter={e => { e.currentTarget.style.color = 'rgba(255,255,255,0.6)'; }}
                onMouseLeave={e => { e.currentTarget.style.color = 'rgba(255,255,255,0.35)'; }}
                >
                  Exit
                </button>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  if (!steps.length) return null;

  const step = steps[currentStep];
  if (!step) return null;
  const progress = ((currentStep + 1) / steps.length) * 100;

  const pal = albumPalettes[currentStep % albumPalettes.length];

  return (
    <>
      {tourStyles}
      {overlay}

      <div style={{
        position: 'fixed', left: '50%',
        ...(isNarrowScreen
          ? (isTitleBarStep
            ? { bottom: 'max(100px, calc(env(safe-area-inset-bottom, 20px) + 80px))', transform: 'translateX(-50%)' }
            : { top: 'max(56px, env(safe-area-inset-top, 56px))', transform: 'translateX(-50%)' })
          : { top: '50%', transform: 'translate(-50%, -50%)' }),
        zIndex: 100002,
        animation: isNarrowScreen
          ? 'tourPanelSlideUpTop 0.5s cubic-bezier(0.16, 1, 0.3, 1)'
          : 'tourPanelSlideUp 0.5s cubic-bezier(0.16, 1, 0.3, 1)',
        width: '90%', maxWidth: 420, pointerEvents: 'none',
      }}>
        <div style={{
          width: '100%', position: 'relative', overflow: 'hidden',
          borderRadius: 16,
          boxShadow: '0 24px 80px rgba(0,0,0,0.7), 0 0 0 1px rgba(255,255,255,0.08)',
          pointerEvents: 'auto',
        }}>
          {/* Album cover header */}
          <div style={{
            position: 'relative', padding: '20px 22px 16px',
            background: pal.bg,
            transition: 'background 0.6s ease',
            minHeight: 100,
          }}>
            {/* Vinyl record peeking from right */}
            <div style={{
              position: 'absolute', right: -28, top: '50%', transform: 'translateY(-50%)',
              width: 96, height: 96, borderRadius: '50%', pointerEvents: 'none',
              background: 'radial-gradient(circle, #222 18%, #111 19%, #1a1a1a 20%, #333 21%, #1a1a1a 40%, #333 41%, #1a1a1a 60%, #333 61%, #1a1a1a 80%, #222 90%)',
              animation: speaking ? 'tourVinylSpin 3s linear infinite' : 'none',
              boxShadow: '-3px 0 16px rgba(0,0,0,0.5)',
              opacity: 0.75,
            }}>
              <div style={{
                position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
                width: 22, height: 22, borderRadius: '50%',
                background: `radial-gradient(circle, ${pal.accent} 20%, rgba(0,0,0,0.3) 21%, ${pal.accent}40 50%, transparent 60%)`,
              }}>
                <div style={{
                  position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
                  width: 5, height: 5, borderRadius: '50%', background: '#111',
                }} />
              </div>
            </div>

            {/* Floating notes when speaking */}
            {speaking && (
              <>
                <div style={{
                  position: 'absolute', top: 14, right: 76, fontSize: 16, pointerEvents: 'none',
                  color: 'rgba(255,255,255,0.2)', animation: 'tourNoteFloat1 2.5s ease-in-out infinite',
                }}>&#9835;</div>
                <div style={{
                  position: 'absolute', bottom: 16, right: 88, fontSize: 12, pointerEvents: 'none',
                  color: 'rgba(255,255,255,0.15)', animation: 'tourNoteFloat2 3s ease-in-out infinite',
                }}>&#9834;</div>
              </>
            )}

            {/* Category and step counter */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
              <div style={{
                width: 5, height: 5, borderRadius: '50%',
                background: speaking ? '#fff' : paused ? '#f59e0b' : 'rgba(255,255,255,0.5)',
                boxShadow: speaking ? '0 0 8px rgba(255,255,255,0.6)' : 'none',
                animation: speaking ? 'tourGlow 1.5s ease-in-out infinite' : 'none',
              }} />
              <span style={{
                fontSize: 10, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase',
                color: pal.label, opacity: 0.8,
              }}>
                {step.category} — {currentStep + 1}/{steps.length}
              </span>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginLeft: 'auto' }}>
                {speaking && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                    {[0,1,2,3,4].map(i => (
                      <div key={i} style={{ width: 2.5, borderRadius: 1, background: 'rgba(255,255,255,0.5)', animation: `tourAudioWave 0.8s ease-in-out ${i * 0.12}s infinite` }} />
                    ))}
                  </div>
                )}
                {paused && <span style={{ fontSize: 9, color: '#f59e0b', fontWeight: 700 }}>PAUSED</span>}
              </div>
            </div>

            {/* Step title — big album-style text */}
            <div style={{
              fontSize: 24, fontWeight: 900, color: '#fff', lineHeight: 1.1,
              letterSpacing: '-0.01em',
              textShadow: '0 2px 12px rgba(0,0,0,0.4)',
              maxWidth: '72%',
            }}>
              {step.title}
            </div>
          </div>

          {/* Progress bar */}
          <div style={{ height: 3, background: 'rgba(255,255,255,0.06)' }}>
            <div style={{
              height: '100%', width: `${progress}%`,
              background: `linear-gradient(90deg, ${pal.accent}, ${pal.accent}cc)`,
              transition: 'width 0.3s ease',
              boxShadow: `0 0 8px ${pal.accent}40`,
            }} />
          </div>

          {/* Content area */}
          <div style={{
            background: '#111', padding: '14px 22px 18px',
          }}>
            <div style={{ fontSize: 13, lineHeight: 1.6, color: 'rgba(255,255,255,0.6)', marginBottom: 14, minHeight: 20 }}>
              {revealText(step.narration, smoothProgress)}
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <button
                onClick={togglePause}
                style={{
                  width: 36, height: 36, borderRadius: 10,
                  border: `1px solid ${pal.accent}30`,
                  background: `${pal.accent}12`, color: pal.accent,
                  cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  transition: 'all 0.2s',
                }}
                onMouseEnter={e => { e.currentTarget.style.background = `${pal.accent}25`; }}
                onMouseLeave={e => { e.currentTarget.style.background = `${pal.accent}12`; }}
              >
                {paused ? (
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><polygon points="5 3 19 12 5 21 5 3" /></svg>
                ) : (
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><rect x="6" y="4" width="4" height="16" /><rect x="14" y="4" width="4" height="16" /></svg>
                )}
              </button>

              <button
                onClick={skipForward}
                style={{
                  width: 36, height: 36, borderRadius: 10,
                  border: `1px solid ${pal.accent}30`,
                  background: `${pal.accent}12`, color: pal.accent,
                  cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  transition: 'all 0.2s',
                }}
                onMouseEnter={e => { e.currentTarget.style.background = `${pal.accent}25`; }}
                onMouseLeave={e => { e.currentTarget.style.background = `${pal.accent}12`; }}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="13 19 22 12 13 5" /><polyline points="2 19 11 12 2 5" /></svg>
              </button>

              {volumeControl}

              <div style={{ flex: 1 }} />

              <button
                onClick={finishTour}
                style={{
                  background: 'none', border: '1px solid rgba(255,255,255,0.1)', cursor: 'pointer',
                  fontSize: 11, fontWeight: 600, color: 'rgba(255,255,255,0.35)', padding: '6px 14px',
                  borderRadius: 8, transition: 'all 0.2s',
                }}
                onMouseEnter={e => { e.currentTarget.style.color = 'rgba(255,255,255,0.6)'; }}
                onMouseLeave={e => { e.currentTarget.style.color = 'rgba(255,255,255,0.35)'; }}
              >
                Exit
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
